import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Clock, Sparkles } from "lucide-react";
import { api, pickLang } from "../lib/api";
import { useSettings } from "../context/ThemeContext";
import { useLang } from "../context/LanguageContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ExperiencePlaceholder from "../components/ExperiencePlaceholder";

const HERO_FALLBACK = "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=2000&auto=format&fit=crop";

export default function Home() {
  const { settings } = useSettings();
  const { t, lang } = useLang();
  const [experiences, setExperiences] = useState([]);
  const [filter, setFilter] = useState("__all");

  useEffect(() => {
    api.get("/experiences").then((r) => setExperiences(r.data || [])).catch(() => {});
  }, []);

  const categories = ["__all", ...Array.from(new Set(experiences.map((e) => pickLang(e, "category", lang)).filter(Boolean)))];
  const visible = filter === "__all" ? experiences : experiences.filter((e) => pickLang(e, "category", lang) === filter);
  const featured = experiences.filter((e) => e.featured).slice(0, 3);
  const catLabel = (c) => c === "__all" ? t("home.filter_all") : c;

  const heroTitle = (lang === "es" && settings.hero_title) ? settings.hero_title : t("home.hero_title_default");
  const heroSub = (lang === "es" && settings.hero_subtitle) ? settings.hero_subtitle : t("home.hero_subtitle_default");
  const heroImg = settings.hero_image || HERO_FALLBACK;

  return (
    <div data-testid="home-page">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-end md:items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImg} alt="Riviera Maya" className="w-full h-full object-cover" />
          <div className="bn-hero-overlay" />
        </div>

        <div className="relative z-10 bn-container px-6 md:px-12 pb-16 md:pb-0 pt-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            className="max-w-3xl"
          >
            <span className="bn-eyebrow" style={{ color: "#F5F3EC" }}>{t("home.eyebrow")}</span>
            <h1
              className="mt-5 md:mt-7 text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight"
              style={{ fontFamily: "var(--bn-font-heading)", color: "#F5F3EC", whiteSpace: "pre-line" }}
              data-testid="hero-title"
            >
              {heroTitle}
            </h1>
            <p className="mt-6 text-base md:text-lg max-w-xl" style={{ color: "rgba(245,243,236,0.85)", fontFamily: "var(--bn-font-body)" }}>
              {heroSub}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/experiencias" className="bn-btn-accent" data-testid="hero-explore-btn">
                {t("home.explore")}
              </Link>
              <a href="#experiences" className="bn-btn-ghost" style={{ color: "#F5F3EC", borderColor: "rgba(255,255,255,0.4)" }} data-testid="hero-scroll-btn">
                {t("home.see_catalog")}
              </a>
            </div>
          </motion.div>
        </div>

        {/* Scroll hint */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2" style={{ color: "#F5F3EC" }}>
          <div className="bn-eyebrow text-[.65rem]">{t("home.scroll")}</div>
          <div className="w-px h-10" style={{ background: "rgba(255,255,255,0.5)" }} />
        </div>
      </section>

      {/* MARQUEE BAND */}
      <section className="py-8 overflow-hidden" style={{ background: "var(--bn-primary)", color: "var(--bn-primary-fg)" }}>
        <div className="marquee whitespace-nowrap">
          {Array.from({ length: 2 }).map((_, idx) => (
            <div key={idx} className="flex items-center gap-12 pr-12">
              {["Isla Mujeres", "Cozumel", "Holbox", "Chichén Itzá", "Yates privados", "Catamaranes", "Xcaret", "Akumal", "Contoy", "Isla Pasión", "Pesca deportiva", "Kuza Park"].map((n, i) => (
                <span key={i} className="flex items-center gap-4 bn-eyebrow text-sm md:text-base tracking-[0.3em]">
                  <Sparkles size={14} style={{ color: "var(--bn-accent)" }} />
                  {n}
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="bn-section">
          <div className="bn-container">
            <div className="flex items-end justify-between mb-10 md:mb-16 flex-wrap gap-6">
              <div>
                <div className="bn-eyebrow">{t("home.featured_eyebrow")}</div>
                <h2 className="bn-heading text-3xl md:text-5xl mt-3 max-w-xl">{t("home.featured_title")}</h2>
              </div>
              <Link to="/experiencias" className="bn-eyebrow inline-flex items-center gap-2" style={{ color: "var(--bn-accent)" }} data-testid="featured-see-all">
                {t("home.see_all")} <ArrowRight size={14} />
              </Link>
            </div>

            <div className="grid md:grid-cols-12 gap-6 md:gap-8">
              {featured.map((exp, idx) => (
                <motion.div
                  key={exp.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: idx * 0.08 }}
                  className={idx === 0 ? "md:col-span-7" : "md:col-span-5"}
                  data-testid={`featured-card-${idx}`}
                >
                  <Link to={`/experiencia/${exp.slug || exp.id}`} className="block bn-card overflow-hidden group">
                    <div className="relative overflow-hidden" style={{ aspectRatio: idx === 0 ? "16/10" : "4/5" }}>
                      {exp.images?.[0] ? (
                        <img
                          src={exp.images[0]}
                          alt={pickLang(exp, "name", lang)}
                          className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                        />
                      ) : (
                        <ExperiencePlaceholder />
                      )}
                    </div>
                    <div className="p-6 md:p-8">
                      <div className="flex items-center justify-between mb-3">
                        <span className="bn-eyebrow" style={{ color: "var(--bn-accent)" }}>{pickLang(exp, "category", lang)}</span>
                        <span className="bn-heading text-lg">${exp.price} {exp.currency || "USD"}</span>
                      </div>
                      <h3 className="bn-heading text-2xl md:text-3xl leading-tight">{pickLang(exp, "name", lang)}</h3>
                      <p className="mt-3 bn-body-font text-sm line-clamp-2" style={{ color: "var(--bn-text-muted)" }}>
                        {pickLang(exp, "short_description", lang)}
                      </p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ALL EXPERIENCES */}
      <section id="experiences" className="bn-section" style={{ background: "var(--bn-surface)" }}>
        <div className="bn-container">
          <div className="mb-10 md:mb-14">
            <div className="bn-eyebrow">Catálogo completo</div>
            <h2 className="bn-heading text-3xl md:text-5xl mt-3">Descubre, reserva, vive.</h2>
          </div>

          <div className="flex gap-2 flex-wrap mb-10">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className="bn-eyebrow px-4 py-2 transition-all"
                style={{
                  borderRadius: "var(--bn-button-radius)",
                  border: "1px solid var(--bn-border)",
                  background: filter === c ? "var(--bn-primary)" : "transparent",
                  color: filter === c ? "var(--bn-primary-fg)" : "var(--bn-text)",
                }}
                data-testid={`filter-${c.toLowerCase()}`}
              >
                {catLabel(c)}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {visible.map((exp, idx) => (
              <motion.div
                key={exp.id}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (idx % 6) * 0.05 }}
                data-testid={`experience-card-${exp.slug}`}
              >
                <Link to={`/experiencia/${exp.slug || exp.id}`} className="block bn-card overflow-hidden group h-full">
                  <div className="relative overflow-hidden aspect-[4/5]">
                    {exp.images?.[0] ? (
                      <img
                        src={exp.images[0]}
                        alt={pickLang(exp, "name", lang)}
                        className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                      />
                    ) : (
                      <ExperiencePlaceholder />
                    )}
                    {exp.featured && (
                      <div className="absolute top-4 left-4 px-3 py-1 bn-eyebrow text-[.6rem]" style={{ background: "var(--bn-accent)", color: "var(--bn-accent-fg)", borderRadius: "var(--bn-button-radius)" }}>
                        {lang === "es" ? "Destacado" : "Featured"}
                      </div>
                    )}
                    <div className="absolute top-4 right-4 px-3 py-1 bn-heading" style={{ background: "var(--bn-bg)", color: "var(--bn-text)", borderRadius: "var(--bn-button-radius)", fontSize: ".95rem" }}>
                      ${exp.price} {exp.currency || "USD"}
                    </div>
                  </div>
                  <div className="p-5 md:p-6">
                    <div className="bn-eyebrow mb-2" style={{ color: "var(--bn-accent)" }}>{pickLang(exp, "category", lang)}</div>
                    <h3 className="bn-heading text-xl md:text-2xl leading-tight">{pickLang(exp, "name", lang)}</h3>
                    <p className="mt-2 text-sm line-clamp-2" style={{ color: "var(--bn-text-muted)" }}>
                      {pickLang(exp, "short_description", lang)}
                    </p>
                    <div className="flex items-center gap-4 mt-4 text-xs" style={{ color: "var(--bn-text-muted)" }}>
                      {pickLang(exp, "duration", lang) && <div className="flex items-center gap-1"><Clock size={12} /> {pickLang(exp, "duration", lang)}</div>}
                      {pickLang(exp, "location", lang) && <div className="flex items-center gap-1"><MapPin size={12} /> {pickLang(exp, "location", lang)}</div>}
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bn-section relative overflow-hidden" style={{ background: "var(--bn-primary)", color: "var(--bn-primary-fg)" }}>
        <div className="bn-container relative z-10">
          <div className="max-w-2xl">
            <div className="bn-eyebrow" style={{ color: "var(--bn-accent)" }}>{t("home.val_eyebrow", { name: settings.val_name || "Val" })}</div>
            <h2 className="mt-4 text-3xl md:text-5xl" style={{ fontFamily: "var(--bn-font-heading)", whiteSpace: "pre-line" }}>
              {t("home.val_title", { name: settings.val_name || "Val" })}
            </h2>
            <p className="mt-5 text-base md:text-lg opacity-80 max-w-xl">
              {t("home.val_subtitle")}
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
